import React, { useState, useEffect } from "react";
import { useLocation, navigate } from "@reach/router";
import ErrorBoundary from "../../ErrorBoundary";
import {
  TableOfContentsHeading,
  TableOfContentsSubHeadingWrapper,
  TableOfContentsSubHeading,
  SubHeadingAnimationBar
} from "./TOCStyledComponents";
import {
  filterHeadingsByDepth,
  mapHeadingsForSubheading,
  sortSubheadingsIntoHeadings,
  isSubheading,
  findParentHeadingFromSubheading
} from "./ToCHelperFunctions";

const TableOfContents = ({ headings }) => {
  const filteredHeadings = filterHeadingsByDepth(headings);
  const mappedHeadings = mapHeadingsForSubheading(filteredHeadings);
  const headingsWithSubheadings = sortSubheadingsIntoHeadings(mappedHeadings);

  // Check for preset heading in the URL
  const location = useLocation();

  // See if a heading has is already present in the URL on page load, to pre-select the correct heading in the nav
  const onLoadHash = location.hash;

  // Set default values for headings and subheadings
  let onLoadHeading = headingsWithSubheadings[0];
  let onLoadSubHeading = null;

  // If hash is present in URL, proceed to update on-load heading values
  if (onLoadHash) {
    // Initally assume on-load hash is heading and format accordingly. 
    // Format on-load hash to match the mapped headings by removing the `#`
    onLoadHeading = {
      id: onLoadHash.replace("#", "")
    };

    // Check if on-load hash is a heading or subheading
    const isOnloadHeadingASubheading = isSubheading(
      onLoadHeading,
      headingsWithSubheadings
    );

    // If on-load hash is in fact a subheading, find its parent heading and update onLoad values
    if (isOnloadHeadingASubheading) {
      onLoadSubHeading = onLoadHeading;
      onLoadHeading = findParentHeadingFromSubheading(
        onLoadSubHeading,
        headingsWithSubheadings
      );
    }
  }

  const [activeHeading, setActiveHeading] = useState(onLoadHeading);
  const [activeSubHeading, setActiveSubHeading] = useState(onLoadSubHeading);
  const [scrollTop, setScrollTop] = useState(0);

  // Handler for ToC clicks to scroll headings smoothly to top of viewport
  useEffect(() => {
    const anchorClickListener = e => {
      e.preventDefault();
      document.querySelector(e.target.getAttribute("href")).scrollIntoView({
        behavior: "smooth"
      });
      /* 
        Set timeout to update navigation with section hash
        Timout required to allow smooth scroll behavior to finish, 
        otherwise page will jump to location. 
        Replace flag required so that back button goes to previous page, 
        rather than previous hash. 
      */
      setTimeout(() => navigate(e.target.href, { replace: true }), 1000);
    };

    const anchorClickSetup = () => {
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener("click", anchorClickListener);
      });
    };

    const anchorClickTeardown = () => {
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.removeEventListener("click", anchorClickListener);
      });
    };
    anchorClickSetup();
    return anchorClickTeardown;
  }, [scrollTop]);

  // Handler to update active headings in ToC as user scrolls through the page
  useEffect(() => {
    const getCurrentHeadingIndex = () => {
      let currentIndex;
      headingsWithSubheadings.forEach((heading, index) => {
        if (heading.id === activeHeading.id) {
          currentIndex = index;
        }
      });
      return currentIndex;
    };

    const elementIsInViewport = element => {
      if (element) {
        const rect = element.getBoundingClientRect();
        return (
          rect.top >= 0 &&
          rect.bottom <=
            (window.innerHeight || document.documentElement.clientHeight)
        );
      }
    };

    const updateNextActiveHeadingOnScroll = () => {
      const currentIndex = getCurrentHeadingIndex();
      const nextIndex = currentIndex + 1;
      const nextHeading =
        nextIndex < headingsWithSubheadings.length
          ? headingsWithSubheadings[nextIndex]
          : headingsWithSubheadings[currentIndex];
      if (
        nextHeading &&
        nextHeading.id &&
        elementIsInViewport(document.querySelector(`#${nextHeading.id}`))
      ) {
        setActiveHeading(nextHeading);
      }
    };

    const updatePreviousActiveHeadingOnScroll = () => {
      const currentIndex = getCurrentHeadingIndex();
      const previousIndex = currentIndex - 1;
      const previousHeading =
        previousIndex > 0
          ? headingsWithSubheadings[previousIndex]
          : headingsWithSubheadings[currentIndex];
      if (
        elementIsInViewport(
          previousHeading &&
            previousHeading.id &&
            document.querySelector(`#${previousHeading.id}`)
        )
      ) {
        setActiveHeading(previousHeading);
      }
    };

    const onScrollListener = e => {
      if (scrollTop < e.target.documentElement.scrollTop) {
        setScrollTop(e.target.documentElement.scrollTop);
        updateNextActiveHeadingOnScroll(activeHeading, activeSubHeading);
      } else {
        setScrollTop(e.target.documentElement.scrollTop);
        updatePreviousActiveHeadingOnScroll(activeHeading, activeSubHeading);
      }
    };

    const onScrollSetup = () =>
      window.addEventListener("scroll", onScrollListener);

    const onScrollTeardown = () =>
      window.removeEventListener("scroll", onScrollListener);

    if (typeof window !== `undefined`) {
      onScrollSetup();
      return onScrollTeardown;
    }
  }, [scrollTop, activeSubHeading, activeHeading, headingsWithSubheadings]);

  return (
    <ErrorBoundary>
      {headingsWithSubheadings.map(heading => {
        return (
          <span key={heading.id}>
            <TableOfContentsHeading
              href={`#${heading.id}`}
              depth={heading.depth}
              isActive={activeHeading && activeHeading.id === heading.id}
              onClick={() => {
                setActiveHeading(heading);
                setActiveSubHeading(null);
              }}
            >
              {heading.value}
            </TableOfContentsHeading>
            {activeHeading && activeHeading.id === heading.id && (
              <TableOfContentsSubHeadingWrapper isActive>
                <SubHeadingAnimationBar isActive />
                {heading.subHeadings.map(subHeading => {
                  return (
                    <TableOfContentsSubHeading
                      key={subHeading.id}
                      href={`#${subHeading.id}`}
                      depth={subHeading.depth}
                      isActive={
                        activeSubHeading &&
                        activeSubHeading.id === subHeading.id
                      }
                      onClick={() => setActiveSubHeading(subHeading)}
                    >
                      {subHeading.value}
                    </TableOfContentsSubHeading>
                  );
                })}
              </TableOfContentsSubHeadingWrapper>
            )}
          </span>
        );
      })}
    </ErrorBoundary>
  );
};

TableOfContents.defaultProps = {
  headings: []
};

export default TableOfContents;
